import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Alert, Badge, Button, Card, Field, Input, Select } from '../ui';
import { api, ApiError } from '../../lib/api';
import { uploadWithProgress } from '../../lib/upload';
import { DOC_KINDS, type DocKind, type Document, type Task } from '../../lib/types';

const statusTone = (s: string) =>
  (s === 'pass'
    ? 'green'
    : s === 'not_compliant' || s === 'failed'
      ? 'red'
      : s === 'verifying'
        ? 'blue'
        : 'slate') as 'green' | 'red' | 'blue' | 'slate';

export function DocumentsTab({
  orgId,
  pid,
  canUpload,
  tasks,
  onChanged,
}: {
  orgId: string;
  pid: string;
  canUpload: boolean;
  tasks: Task[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const base = `/api/v1/orgs/${orgId}/products/${pid}/documents`;
  const [docs, setDocs] = useState<Document[] | null>(null);
  const [msg, setMsg] = useState<{ tone: 'red' | 'green'; text: string } | null>(null);
  const [form, setForm] = useState<{
    file: File | null;
    name: string;
    kind: DocKind | '';
    taskKey: string;
    supersedesDocId: string;
  }>({ file: null, name: '', kind: '', taskKey: '', supersedesDocId: '' });
  const [pct, setPct] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const load = useCallback(
    () => api<{ documents: Document[] }>(base).then((r) => setDocs(r.documents)),
    [base],
  );
  useEffect(() => {
    void load();
  }, [load]);
  // poll while VaultSage is still converting
  useEffect(() => {
    if (!docs?.some((d) => d.vaultsageProcessing === 'pending' || d.vaultsageProcessing === 'processing'))
      return;
    const id = setTimeout(() => void load(), 4000);
    return () => clearTimeout(id);
  }, [docs, load]);

  const fail = (e: unknown) =>
    setMsg({
      tone: 'red',
      text:
        e instanceof ApiError
          ? t([`docs.err_${e.code}`, 'docs.err_generic'], { code: e.code }) +
            (e.code === 'UNSUPPORTED_FILE_TYPE' || e.code === 'INVALID_FORMULATION' ? ` ${e.message}` : '')
          : String(e),
    });
  const upload = async () => {
    if (!form.file) return;
    setMsg(null);
    setPct(0);
    try {
      const fields: Record<string, string> = {
        name: form.name,
        kind: form.kind,
        taskKey: form.taskKey,
        supersedesDocId: form.supersedesDocId,
      };
      const d = await uploadWithProgress<Document>(base, form.file, fields, setPct);
      setMsg({ tone: 'green', text: `${d.id} - ${t('docs.status_uploaded')}` });
      setForm({ file: null, name: '', kind: '', taskKey: '', supersedesDocId: '' });
      if (fileRef.current) fileRef.current.value = '';
      await load();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setPct(null);
    }
  };
  const [busyId, setBusyId] = useState<string | null>(null);
  /** soft exclude / include (the document is kept; only the verification counts change) */
  const toggleExcluded = async (d: Document) => {
    setMsg(null);
    setBusyId(d.id);
    try {
      await api(`${base}/${d.id}/exclude`, { method: 'POST', json: { excluded: !d.excluded } });
      await load();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  };
  // counted = current versions that are not excluded (superseded versions are history)
  const summary = useMemo(() => {
    const cur = (docs ?? []).filter((d) => !d.supersededByDocId);
    const counted = cur.filter((d) => !d.excluded);
    const n = (st: string[]) => counted.filter((d) => st.includes(d.status)).length;
    return {
      counted: counted.length,
      verified: n(['pass', 'not_compliant']),
      pass: n(['pass']),
      notCompliant: n(['not_compliant']),
      excluded: cur.length - counted.length,
    };
  }, [docs]);
  const actionable = tasks.filter((x) => x.actionable);
  return (
    <div className="space-y-4">
      {canUpload && (
        <Card
          title={
            form.supersedesDocId
              ? `${t('docs.newVersion')} (${t('docs.supersedes', { id: form.supersedesDocId })})`
              : t('docs.upload')
          }
        >
          <div className="grid min-w-0 gap-3 md:grid-cols-[1.4fr_1fr_0.8fr_1.2fr]">
            <Field label={t('docs.upload')} hint={t('docs.dropHint')}>
              <input
                ref={fileRef}
                type="file"
                className="block w-full min-w-0 text-sm file:mr-2 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-2 file:py-1 file:text-sm"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.xlsx,.xls,.docx,.doc,.pptx,.hwp,.hwpx"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setForm((s) => ({
                    ...s,
                    file: f,
                    name: s.name || (f ? f.name.replace(/\.[^.]+$/, '') : ''),
                  }));
                }}
              />
            </Field>
            <Field label={t('docs.nameLabel')}>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                maxLength={80}
              />
            </Field>
            <Field label={t('docs.kind')}>
              <Select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as DocKind | '' })}
              >
                <option value="">auto</option>
                {DOC_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`docs.kind_${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('docs.task')}>
              <Select value={form.taskKey} onChange={(e) => setForm({ ...form, taskKey: e.target.value })}>
                <option value="">{t('docs.noTask')}</option>
                {actionable.map((x) => (
                  <option key={x.key} value={x.key} title={x.title}>
                    {x.key} · {x.title.length > 28 ? x.title.slice(0, 27) + '…' : x.title}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button onClick={() => void upload()} disabled={!form.file || pct !== null}>
              {pct !== null ? t('docs.uploading', { pct }) : t('docs.upload')}
            </Button>
            {form.supersedesDocId && (
              <Button variant="secondary" onClick={() => setForm({ ...form, supersedesDocId: '' })}>
                {t('products.cancel')}
              </Button>
            )}
            {pct !== null && (
              <div className="h-2 flex-1 rounded bg-slate-200">
                <div className="h-2 rounded bg-emerald-500" style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        </Card>
      )}
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {!!docs?.length && (
        <p className="text-sm text-slate-600" aria-live="polite">
          {t('docs.countLine', {
            verified: summary.verified,
            counted: summary.counted,
            pass: summary.pass,
            nc: summary.notCompliant,
          })}
          {summary.excluded > 0 && (
            <span className="ml-2 text-slate-500">
              · {t('docs.excludedCount', { count: summary.excluded })}
            </span>
          )}
        </p>
      )}
      <Card>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2">{t('docs.id')}</th>
              <th>{t('docs.nameLabel')}</th>
              <th>{t('docs.kind')}</th>
              <th>{t('docs.task')}</th>
              <th>{t('docs.status')}</th>
              <th>{t('docs.processing')}</th>
              <th>{t('docs.uploadedBy')}</th>
              <th className="text-right">{t('members.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {docs?.map((d) => (
              <tr key={d.id} className={d.supersededByDocId || d.excluded ? 'bg-slate-50 opacity-60' : ''}>
                <td className="py-2 font-mono text-xs">
                  <Link className="text-emerald-700 underline" to={`/products/${pid}/documents/${d.id}`}>
                    {d.id}
                  </Link>
                </td>
                <td>
                  {d.name}
                  <span className="ml-1 text-xs text-slate-400">{d.ext}</span>
                  {d.supersedesDocId && (
                    <div className="text-xs text-slate-500">
                      {t('docs.supersedes', { id: d.supersedesDocId })}
                    </div>
                  )}
                </td>
                <td>
                  <Badge>{t(`docs.kind_${d.kind}`)}</Badge>
                </td>
                <td className="font-mono text-xs">{d.taskKey ?? '-'}</td>
                <td>
                  <Badge tone={statusTone(d.status)}>{t(`docs.status_${d.status}`)}</Badge>
                  {d.latestCheckId && (
                    <div className="text-[10px] text-slate-500">
                      {(d.latestResult?.jurisdictions ?? ['us-fda'])
                        .map((j) => (j === 'kr-mfds' ? 'MFDS(KR)' : 'FDA(US)'))
                        .join(' + ')}
                    </div>
                  )}
                  {d.excluded && (
                    <span
                      className="ml-1"
                      title={t('docs.excludedBy', {
                        who: d.excludedByEmail ?? '',
                        date: (d.excludedAt ?? '').slice(0, 10),
                      })}
                    >
                      <Badge tone="amber">{t('docs.excluded')}</Badge>
                    </span>
                  )}
                </td>
                <td className="text-xs text-slate-500">{d.vaultsageProcessing}</td>
                <td className="text-xs">
                  {d.uploadedByEmail}
                  <div className="text-slate-400">{d.uploadedAt.slice(0, 16).replace('T', ' ')}</div>
                </td>
                <td className="text-right">
                  <div className="flex justify-end gap-1">
                    <a
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      href={`${base}/${d.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('docs.open')}
                    </a>
                    <a
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      href={`${base}/${d.id}/file?download=1`}
                    >
                      {t('docs.download')}
                    </a>
                    {canUpload && (
                      <Button
                        variant="ghost"
                        className="text-xs"
                        disabled={busyId === d.id}
                        title={d.excluded ? t('docs.includeHint') : t('docs.excludeHint')}
                        onClick={() => void toggleExcluded(d)}
                      >
                        {d.excluded ? t('docs.include') : t('docs.exclude')}
                      </Button>
                    )}
                    {canUpload && !d.supersededByDocId && (
                      <Button
                        variant="secondary"
                        className="text-xs"
                        onClick={() =>
                          setForm({
                            ...form,
                            supersedesDocId: d.id,
                            kind: d.kind,
                            taskKey: d.taskKey ?? '',
                            name: d.name,
                          })
                        }
                      >
                        {t('docs.newVersion')}
                      </Button>
                    )}
                    {['label_artwork', 'label_copy', 'sfp'].includes(d.kind) ? (
                      <Link
                        to={`/products/${pid}/documents/${d.id}`}
                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                      >
                        {d.latestCheckId || d.status !== 'uploaded' ? t('review.title') : t('docs.verify')}
                      </Link>
                    ) : (
                      <Button
                        variant="secondary"
                        className="text-xs"
                        disabled
                        title={t('review.notReviewable')}
                      >
                        {t('docs.verify')}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {docs?.length === 0 && (
              <tr>
                <td colSpan={8} className="py-6 text-center text-slate-500">
                  {t('docs.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
